import { HeadlessTaskStatus } from "@jugl-web/domain-resources/tasks/providers/HeadlessTaskStatus";
import {
  WhatsappTemplateCategory,
  WhatsappTemplateModel,
  WhatsappTemplateStatus,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import {
  Alert,
  DataLoadingWrapper,
  EmptyListContent,
  Menu,
  PlainButton,
  TableGrid,
  Tooltip,
} from "@jugl-web/ui-components";
import { useToast, useTranslations } from "@jugl-web/utils";
import { useSelector } from "react-redux";
import { selectUserId } from "@web-src/features/auth/authSlice";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import React, { useCallback, useRef, useState } from "react";
import { Lottie } from "@jugl-web/utils/utils/Lottie";
import { useTaskBoardList } from "@jugl-web/domain-resources/tasks/hooks/useTaskBoardList";
import { useEffectOnce } from "react-use";
import { useScreenTransitionManager } from "@jugl-web/utils/utils/ScreenTransitionManager";
import alertAnimation from "./assets/alert-animation.json";
import { ReactComponent as DeleteIcon } from "./assets/delete.svg";
import { ReactComponent as EditIcon } from "./assets/edit.svg";
import { ReactComponent as InfoIcon } from "./assets/info.svg";
import { ReactComponent as MoreIcon } from "./assets/more.svg";
import { ReactComponent as AddIcon } from "./assets/add.svg";
import { ReactComponent as TaskIcon } from "./assets/task.svg";
import { ReactComponent as MessageIcon } from "./assets/message.svg";
import { TemplateApprovalTile } from "./components/TemplateApprovalTile";
import { TemplateNameTile } from "./components/TemplateNameTile";
import { TemplateStatusTile } from "./components/TemplateStatusTile";
import { ManageAccountsDrawerScreenToParametersMap } from "../../../../types";

type PageState = {
  requestId: number;
  nextPageId?: string;
  hasMore?: boolean;
};

export const WhatsappTemplatesTab: React.FC<{
  templateCategory: WhatsappTemplateCategory;
}> = ({ templateCategory }) => {
  const { t } = useTranslations();
  const { toast } = useToast();
  const { transitionTo } =
    useScreenTransitionManager<ManageAccountsDrawerScreenToParametersMap>();
  const [templateIdToDelete, setTemplateIdToDelete] = useState<string>();
  const currentPageState = useRef<PageState | null>(null);

  const { entityId } = useEntitySelectedProvider();
  const [whatsappTemplates, setWhatsappTemplates] = useState<
    WhatsappTemplateModel[]
  >([]);
  const { integrationsApi } = useRestApiProvider();

  const meId = useSelector(selectUserId);
  const { accessibleBoards } = useTaskBoardList({ entityId, meId: meId || "" });

  const { subscriptionRequiredAction } = useEntityProvider();

  const [getWhatsappTemplates, { isFetching, isLoading, isError, data }] =
    integrationsApi.useLazyGetWhatsappTemplatesQuery();

  const [
    deleteWhatsappTemplate,
    { isLoading: isDeleteWhatsappTemplateLoading },
  ] = integrationsApi.useDeleteWhatsappTemplateMutation();

  const consecutiveEmptyFetches = useRef<number>(0);

  const loadNextPage = useCallback(async () => {
    let nextPageId: string | undefined;
    if (currentPageState.current) {
      nextPageId = currentPageState.current.nextPageId;
    }

    const nextPageState: PageState = {
      requestId: Math.random(),
      nextPageId,
    };

    currentPageState.current = nextPageState;

    const result = await getWhatsappTemplates({
      entityId,
      params: {
        page: nextPageId,
        type: templateCategory,
      },
    });

    if (currentPageState.current?.requestId !== nextPageState.requestId) {
      return;
    }

    if ("error" in result && result.error) {
      currentPageState.current = nextPageState;
      return;
    }

    if ("data" in result && result.data && result.data.data) {
      const responseData = result.data.data;
      nextPageState.hasMore = !!result.data.next_page;
      nextPageState.nextPageId = result.data.next_page || undefined;

      if (responseData.length === 0 && nextPageState.nextPageId) {
        consecutiveEmptyFetches.current += 1;
        const MAX_CONSECUTIVE_EMPTY_FETCHES = 5;
        if (consecutiveEmptyFetches.current >= MAX_CONSECUTIVE_EMPTY_FETCHES) {
          nextPageState.hasMore = false;
          currentPageState.current = nextPageState;
          consecutiveEmptyFetches.current = 0; // Reset for next user action
          return;
        }
        loadNextPage();
        return;
      }

      consecutiveEmptyFetches.current = 0;

      setWhatsappTemplates((prev) =>
        nextPageId ? [...prev, ...responseData] : responseData
      );

      currentPageState.current = nextPageState;
    }
  }, [entityId, getWhatsappTemplates, templateCategory]);

  useEffectOnce(() => {
    loadNextPage();
  });

  const handleTemplateDelete = useCallback(async () => {
    if (!templateIdToDelete) return;
    const response = await deleteWhatsappTemplate({
      entityId,
      templateId: templateIdToDelete,
    });

    if (response && "data" in response) {
      setTemplateIdToDelete(undefined);
      toast(
        t({
          id: "client-support-page.whatsapp-update-was-deleted",
          defaultMessage: "WhatsApp Update was deleted",
        })
      );
      setWhatsappTemplates([]);
      currentPageState.current = null;
      loadNextPage();
    }
  }, [
    deleteWhatsappTemplate,
    entityId,
    templateIdToDelete,
    t,
    toast,
    loadNextPage,
  ]);

  return (
    <>
      <DataLoadingWrapper
        onRetry={loadNextPage}
        isError={isError}
        isLoading={
          isLoading ||
          !data ||
          (isFetching && !currentPageState.current?.nextPageId)
        }
        isFetching={isFetching}
      >
        {whatsappTemplates.length ? (
          <TableGrid
            variant="grid"
            data={whatsappTemplates}
            id="client-support"
            onReachEnd={() =>
              currentPageState.current?.hasMore && !isLoading && !isFetching
                ? loadNextPage()
                : undefined
            }
            columns={[
              {
                id: "name-and-content",
                title: t({
                  id: "client-support-page.name-and-content",
                  defaultMessage: "Name & content",
                }),
                grow: true,
                content: ({ name, body }) => (
                  <TemplateNameTile text={body} name={name} />
                ),
              },
              {
                id: "board",
                title: t({
                  id: "client-support-page.board",
                  defaultMessage: "Board",
                }),
                isHidden: templateCategory !== WhatsappTemplateCategory.tasks,
                grow: true,
                content: ({ board_id }) => {
                  const board = board_id
                    ? accessibleBoards.find((item) => item.id === board_id)
                    : null;
                  return (
                    <span className="flex items-center gap-1.5 font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark">
                      {board?.name || "-"}
                    </span>
                  );
                },
              },
              {
                id: "action-trigger",
                title: t({
                  id: "client-support-page.action-trigger",
                  defaultMessage: "Action Trigger",
                }),
                grow: true,
                isHidden: templateCategory === WhatsappTemplateCategory.message,
                content: ({ board_id, action }) => (
                  <span className="flex items-center gap-1.5 font-secondary text-sm leading-[140%] tracking-[0.14px] text-dark">
                    {t(
                      {
                        id: "client-support-page.trigger-status-set",
                        defaultMessage: "Status set to {action}",
                      },
                      {
                        action: action ? (
                          <HeadlessTaskStatus
                            entityId={entityId}
                            boardId={board_id || null}
                            supportForeignBoardStatus
                            statusId={action}
                          >
                            {({ safeStatus, isLoading: isStatusLoading }) =>
                              isStatusLoading ? (
                                <div className="inline-block h-4 w-16 animate-pulse rounded-full bg-grey-100" />
                              ) : (
                                <span className="underline">
                                  {safeStatus.text}
                                </span>
                              )
                            }
                          </HeadlessTaskStatus>
                        ) : (
                          t({ id: "common.unknown", defaultMessage: "Unknown" })
                        ),
                      }
                    )}
                  </span>
                ),
              },
              {
                id: "status",
                title: (
                  <Tooltip
                    placement="bottom"
                    renderTrigger={({ props, ref }) => (
                      <div
                        ref={ref}
                        {...props}
                        className="flex items-center gap-2.5"
                      >
                        <span>
                          {t({
                            id: "client-support-page.status",
                            defaultMessage: "Status",
                          })}
                        </span>
                        <InfoIcon />
                      </div>
                    )}
                  >
                    <div className="flex flex-col gap-2.5">
                      <span>
                        {t({
                          id: "client-support-page.template-active-mode-description",
                          defaultMessage:
                            "🟢 If Message Template is Active, you can send this update to your Customers anytime",
                        })}
                      </span>
                      <span>
                        {t({
                          id: "client-support-page.template-inactive-mode-description",
                          defaultMessage:
                            "⚪ If Message Template is Inactive, it will not be visible in the list of Updates that can be sent to the Customers",
                        })}
                      </span>
                    </div>
                  </Tooltip>
                ),
                grow: true,
                content: ({ enabled, id }) => (
                  <TemplateStatusTile
                    status={enabled}
                    templateId={id}
                    onChange={(status: boolean) =>
                      setWhatsappTemplates((prev) =>
                        prev.map((template) =>
                          template.id === id
                            ? {
                                ...template,
                                enabled: status,
                              }
                            : template
                        )
                      )
                    }
                  />
                ),
              },
              {
                id: "whatsapp-approval",
                title: t({
                  id: "client-support-page.whatsapp-approval",
                  defaultMessage: "WhatsApp Approval",
                }),
                grow: true,
                className: "p-[3px]",
                content: ({ status }) => (
                  <TemplateApprovalTile status={status} />
                ),
              },
              {
                id: "settings",
                title: t({
                  id: "client-support-page.settings",
                  defaultMessage: "Settings",
                }),
                className: "flex justify-center w-full",
                content: ({ id, status }) => (
                  <Menu
                    placement="bottom-end"
                    autoClose
                    renderTrigger={({ Trigger, triggerRef }) => (
                      <Trigger
                        as={PlainButton}
                        ref={triggerRef}
                        className="mt-1"
                        disabled={status === WhatsappTemplateStatus.deleted}
                      >
                        <MoreIcon />
                      </Trigger>
                    )}
                    className="py-2"
                    sections={[
                      [
                        {
                          id: "edit-update",
                          label: t({
                            id: "client-support-page.edit-update",
                            defaultMessage: "Edit Update",
                          }),
                          icon: <EditIcon />,
                          onSelect: subscriptionRequiredAction(() =>
                            transitionTo({
                              name: "whatsappTemplateForm",
                              templateId: id,
                              templateCategory,
                            })
                          ),
                        },
                        {
                          id: "delete-update",
                          label: t({
                            id: "client-support-page.delete-update",
                            defaultMessage: "Delete Update",
                          }),
                          icon: <DeleteIcon />,
                          onSelect: subscriptionRequiredAction(() =>
                            setTemplateIdToDelete(id)
                          ),
                        },
                      ],
                    ]}
                  />
                ),
              },
            ]}
          />
        ) : (
          <EmptyListContent
            type="custom"
            className="w-[500px]"
            customImg={
              templateCategory === WhatsappTemplateCategory.tasks ? (
                <TaskIcon />
              ) : (
                <MessageIcon />
              )
            }
            customTitle={
              templateCategory === WhatsappTemplateCategory.tasks
                ? t({
                    id: "client-support-page.wa-task-updates",
                    defaultMessage: "WhatsApp Integration for Task updates ✨",
                  })
                : t({
                    id: "client-support-page.wa-message-templates",
                    defaultMessage: "WA Message Templates for Your Clients✨",
                  })
            }
            customSubtitle={
              templateCategory === WhatsappTemplateCategory.tasks
                ? t({
                    id: "client-support-page.wa-task-updates-description",
                    defaultMessage:
                      "Create messages templates that will be sent to your Clients through WhatsApp as Task Updates",
                  })
                : t({
                    id: "client-support-page.wa-message-templates-description",
                    defaultMessage:
                      "Create Message Templates, that you can manually send to the Client any time, using CRM WhatsApp chat 📩",
                  })
            }
            customButton={{
              text: t({
                id: "client-support-page.create-template",
                defaultMessage: "Create Template",
              }),
              className: "h-10",
              iconEnd: <AddIcon />,
              onClick: subscriptionRequiredAction(() =>
                transitionTo({
                  name: "whatsappTemplateForm",
                  templateCategory,
                })
              ),
            }}
          />
        )}
      </DataLoadingWrapper>
      <Alert
        isOpen={!!templateIdToDelete}
        onRequestClose={() => setTemplateIdToDelete(undefined)}
        img={
          <Lottie
            options={{ animationData: alertAnimation }}
            height={70}
            width={70}
          />
        }
        title={t({
          id: "common.confirm-action",
          defaultMessage: "Confirm Action",
        })}
        content={t({
          id: "client-support-page.delete-update-confirmation-message",
          defaultMessage:
            "If you delete this Update, you will no longer be able to restore it",
        })}
        buttons={[
          {
            text: t({
              id: "common.cancel",
              defaultMessage: "Cancel",
            }),
            role: "close",
          },
          {
            text: t({
              id: "common.delete",
              defaultMessage: "Delete",
            }),
            color: "tertiary",
            isDisabled: isDeleteWhatsappTemplateLoading,
            onClick: handleTemplateDelete,
          },
        ]}
      />
    </>
  );
};
